// Utility functions to parse and extract passages from text files

/**
 * Determines the performance level based on percentile
 * @param {number} percentile - The percentile value (0-100)
 * @returns {string} - 'low', 'middle', or 'high'
 */
export const getPerformanceLevel = (percentile) => {
  if (percentile <= 33) return 'low';
  if (percentile <= 66) return 'middle';
  return 'high';
};

/**
 * Parses passage text and extracts relevant passage based on performance level
 * @param {string} passageText - Raw text from passage file
 * @param {string} performanceLevel - 'low', 'middle', or 'high'
 * @returns {object} - Parsed passage with title and content
 */
export const parsePassage = (passageText, performanceLevel) => {
  if (!passageText) return null;

  const lines = passageText.split('\n');
  let currentSection = null;
  let sections = {};
  let currentContent = [];

  for (let line of lines) {
    line = line.trim();
    
    // Skip empty lines and code/metadata
    if (!line || line.startsWith('#') || line.startsWith('fig,') || line.startsWith('ax.')) {
      continue;
    }

    // Check for section headers (emoji + title)
    if (line.match(/^[🟫🟥🟧🟨🟦🔴🟦🟨🔵🟢🎯]/)) {
      // Save previous section
      if (currentSection && currentContent.length > 0) {
        sections[currentSection] = {
          title: currentSection,
          content: currentContent.join(' ').trim()
        };
      }
      
      // Start new section
      currentSection = line;
      currentContent = [];
    } else if (currentSection && line.length > 0) {
      // Add content to current section
      currentContent.push(line);
    }
  }

  // Save last section
  if (currentSection && currentContent.length > 0) {
    sections[currentSection] = {
      title: currentSection,
      content: currentContent.join(' ').trim()
    };
  }

  // Find the appropriate section based on performance level
  const levelKeywords = {
    low: ['Low', 'low', '0–33', '0-33'],
    middle: ['Middle', 'middle', '34–66', '34-66', 'average'],
    high: ['High', 'high', '67–100', '67-100']
  };

  const targetKeywords = levelKeywords[performanceLevel] || [];
  
  for (let [sectionKey, sectionData] of Object.entries(sections)) {
    const hasMatchingKeyword = targetKeywords.some(keyword => 
      sectionKey.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (hasMatchingKeyword) {
      return {
        title: sectionData.title,
        content: sectionData.content,
        level: performanceLevel
      };
    }
  }

  // If no specific level found, return the first available section
  const firstSection = Object.values(sections)[0];
  return firstSection ? {
    title: firstSection.title,
    content: firstSection.content,
    level: 'general'
  } : null;
};

/**
 * Gets the appropriate passage for a construct based on percentile
 * @param {string} constructType - The construct type (e.g., 'Persistence', 'Resilience')
 * @param {number} percentile - The percentile value
 * @param {object} passageTexts - Object containing passage texts for different constructs
 * @returns {object|null} - Parsed passage or null if not found
 */
export const getConstructPassage = (constructType, percentile, passageTexts) => {
  const performanceLevel = getPerformanceLevel(percentile);
  const passageText = passageTexts[constructType];
  
  if (!passageText) return null;
  
  return parsePassage(passageText, performanceLevel);
};

/**
 * Handles special cases for resilience quadrant-based passages
 * @param {number} positivePercentile - Positive attribution percentile
 * @param {number} negativePercentile - Negative attribution percentile
 * @param {string} resiliencePassageText - Raw resilience passage text
 * @returns {object|null} - Parsed resilience passage
 */
export const getResilienceQuadrantPassage = (positivePercentile, negativePercentile, resiliencePassageText) => {
  if (!resiliencePassageText) return null;

  const lines = resiliencePassageText.split('\n');
  let sections = {};
  let currentSection = null;
  let currentContent = [];

  for (let line of lines) {
    line = line.trim();
    
    // Skip empty lines and code
    if (!line || line.startsWith('#') || line.startsWith('fig,') || line.startsWith('ax.') || line.startsWith('plt.')) {
      continue;
    }

    // Check for quadrant section headers
    if (line.match(/^🟥|^🟧|^🟨|^🟦/) && (
      line.includes('Optimistic') || 
      line.includes('Prideful') || 
      line.includes('Humble') || 
      line.includes('Pessimistic')
    )) {
      // Save previous section
      if (currentSection && currentContent.length > 0) {
        sections[currentSection] = {
          title: currentSection,
          content: currentContent.join(' ').trim()
        };
      }
      
      // Start new section
      currentSection = line;
      currentContent = [];
    } else if (currentSection && line.length > 0 && !line.startsWith('(')) {
      // Add content to current section (skip parenthetical descriptions)
      currentContent.push(line);
    }
  }

  // Save last section
  if (currentSection && currentContent.length > 0) {
    sections[currentSection] = {
      title: currentSection,
      content: currentContent.join(' ').trim()
    };
  }

  // Determine quadrant based on percentiles
  let quadrantType;
  if (positivePercentile > 50 && negativePercentile > 50) {
    quadrantType = 'Optimistic';
  } else if (positivePercentile > 50 && negativePercentile <= 50) {
    quadrantType = 'Prideful';
  } else if (positivePercentile <= 50 && negativePercentile > 50) {
    quadrantType = 'Humble';
  } else {
    quadrantType = 'Pessimistic';
  }

  // Find matching section
  for (let [sectionKey, sectionData] of Object.entries(sections)) {
    if (sectionKey.toLowerCase().includes(quadrantType.toLowerCase())) {
      return {
        title: sectionData.title,
        content: sectionData.content,
        level: quadrantType.toLowerCase(),
        quadrant: true
      };
    }
  }

  return null;
};
